import { Text, View, ScrollView, RefreshControl } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { supabase } from '@/data/client';
import { manuallyAdvanceStep, skipDelayStep, pauseEnrollment, resumeEnrollment, deleteEnrollment, retryPathStep } from '@/data/paths';
import { resumeParkedAutomationStep } from '@/data/automations';
import { describeStepWaitState, describeCapturedInput, describeStepOutput, isStepParkedForReview, isApprovalStep, stepBranches } from '@/utils/automationBuilder';
import { useLocalSearchParams, useRouter } from 'expo-router';
import usePlatform from '@/utils/hooks/usePlatform';
import { StyleSheet } from 'react-native-unistyles';
import { showAlert } from '@/utils/showAlert';
import { redirectToDefault } from '@/utils/constants/linking';
import Avatar from '@/components/People/Avatar';
import SolidButton from '@/components/General/SolidButton';
import PathFlowTimeline from '@/components/Paths/PathFlowTimeline';
import PathFlowEditor from '@/components/Paths/PathFlowEditor';
import { resolveTaskStepInfo } from '@/components/Paths/flow/actionValues';
import { computePathProgress } from '@/components/Paths/pathProgress';
import { AUTOMATION_DUE_TYPES } from '@/utils/constants/taskTypes';
import ActionButton from '@/components/General/ActionButton';
import ModalWrapper from '@/components/General/ModalWrapper';
import TaskCard from '@/components/Tasks/TaskCard';

const STATUS_CONFIG = {
    ACTIVE: { label: 'Active', colorVariant: 'primary' },
    COMPLETED: { label: 'Completed', colorVariant: 'success' },
    CANCELLED: { label: 'Cancelled', colorVariant: 'error' },
    PAUSED: { label: 'Paused', colorVariant: 'warning' },
};

export default function EnrollmentDetailScreen() {
    const { enrollmentID, pathID } = useLocalSearchParams();
    const router = useRouter();
    const queryClient = useQueryClient();
    const { isWeb } = usePlatform();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: path, isFetching } = Query.paths.byId({
        id: pathID,
        options: {
            refetchInterval: (query) => {
                const en = query.state.data?.Enrollments?.find(e => e.id === enrollmentID);
                return en && en.status !== 'COMPLETED' ? 10000 : false;
            },
        },
    });

    const steps = useMemo(() => path?.Steps || [], [path]);
    const edges = useMemo(() => path?.Edges || [], [path]);
    const pathsTaskQuery = Query.tasks.byDueType({ dueType: AUTOMATION_DUE_TYPES, options: { staleTime: Infinity } });
    const { data: pathAssignmentMembers } = useQuery({
        queryKey: ['pathStepAssignmentMembers', pathID],
        queryFn: async () => {
            const { data, error } = await supabase.from('TaskAssignmentMembers')
                .select('*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
                .eq('pathID', pathID)
                .order('position', { ascending: true });
            if (error) return [];
            return data || [];
        },
        enabled: !!pathID,
        staleTime: 1000 * 60 * 5,
    });
    const taskLookups = useMemo(() => {
        const tasks = new Map((pathsTaskQuery?.data || []).map(task => [task.id, task]));
        const membersByTask = new Map();
        (pathAssignmentMembers || []).forEach(member => {
            if (!membersByTask.has(member.taskID)) membersByTask.set(member.taskID, []);
            membersByTask.get(member.taskID).push(member);
        });
        return { tasks, membersByTask };
    }, [pathsTaskQuery?.data, pathAssignmentMembers]);
    const enrollments = useMemo(() => path?.Enrollments || [], [path]);
    const enrollment = useMemo(() => enrollments.find(e => e.id === enrollmentID), [enrollments, enrollmentID]);

    const enrollmentStepStatus = useMemo(() => {
        const map = {};
        const enrollmentSteps = enrollment?.EnrollmentSteps || [];
        enrollmentSteps.forEach(es => {
            map[es.pathStepID] = es.status;
        });
        // A completed DELAY whose target is still WAITING is really "delayed":
        // present the delay as the current step and the target as upcoming.
        enrollmentSteps.forEach(es => {
            if (es.status !== 'WAITING') return;
            (edges || []).forEach(e => {
                if (e.targetStepID !== es.pathStepID) return;
                const sourceStep = steps.find(s => s.id === e.sourceStepID);
                if (sourceStep?.stepType === 'DELAY' && map[e.sourceStepID] === 'COMPLETED') {
                    map[e.sourceStepID] = 'WAITING';
                    map[es.pathStepID] = 'PENDING';
                }
            });
        });
        return map;
    }, [enrollment, edges, steps]);

    // Per-step enrollment detail for TASK steps: actual assignee (when the step
    // has run) + currentAssigneePosition (for next-assignee prediction).
    const enrollmentStepDetails = useMemo(() => {
        const map = {};
        (enrollment?.EnrollmentSteps || []).forEach(es => {
            const assignees = es.TaskInstance?.Assignees || [];
            const acceptedAssignee = assignees.find(a => a.assignmentResponse === 'ACCEPTED')
                || assignees[0]
                || null;
            map[es.pathStepID] = {
                status: es.status,
                currentAssigneePosition: es.currentAssigneePosition,
                errorMsg: es.errorMsg,
                assignee: acceptedAssignee ? { ...acceptedAssignee, ...acceptedAssignee.Person } : null,
            };
        });
        return map;
    }, [enrollment]);

    const outOfScopeBranches = useMemo(() => {
        const set = new Set();
        const edgeMap = {};
        (edges || []).forEach(e => {
            if (!edgeMap[e.sourceStepID]) edgeMap[e.sourceStepID] = [];
            edgeMap[e.sourceStepID].push(e);
        });

        steps.forEach(step => {
            if (step.stepType !== 'CONDITION' && !isApprovalStep(step)) return;
            if (enrollmentStepStatus[step.id] !== 'COMPLETED') return;

            const stepEdges = edgeMap[step.id] || [];
            // CONDITION → config.branches; REQUEST_APPROVAL → fixed
            // Approved/Denied branch list (no default branch).
            const branches = stepBranches(step);

            branches.forEach(branch => {
                const branchEdge = stepEdges.find(e => e.branchID === branch.id);
                const targetId = branchEdge?.targetStepID;
                const targetStatus = targetId ? enrollmentStepStatus[targetId] : undefined;
                if (!targetStatus || targetStatus === 'PENDING') {
                    set.add(`${step.id}:${branch.id}`);
                }
            });

            if (step.stepType === 'CONDITION') {
                const defaultEdge = stepEdges.find(e => e.branchID === 'default' || (!e.branchID && e.branchLabel === 'No Match'));
                const defaultTargetId = defaultEdge?.targetStepID;
                const defaultTargetStatus = defaultTargetId ? enrollmentStepStatus[defaultTargetId] : undefined;
                if (!defaultTargetStatus || defaultTargetStatus === 'PENDING') {
                    set.add(`${step.id}:default`);
                }
            }
        });

        return set;
    }, [steps, edges, enrollmentStepStatus]);

    const advanceStepMutation = useMutation({
        mutationFn: ({ enrollmentStepID }) => manuallyAdvanceStep({ enrollmentStepID, completedBy: loggedInUser?.id }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
        },
    });

    const skipDelayMutation = useMutation({
        mutationFn: ({ enrollmentStepID }) => skipDelayStep({ enrollmentStepID, completedBy: loggedInUser?.id }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
        },
    });

    const retryStepMutation = useMutation({
        mutationFn: ({ enrollmentStepID }) => retryPathStep({ enrollmentStepID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
        },
    });

    const pauseMutation = useMutation({
        mutationFn: () => pauseEnrollment({ enrollmentID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
        },
    });

    const resumeMutation = useMutation({
        mutationFn: () => resumeEnrollment({ enrollmentID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
        },
    });

    // v2 parked-step resume (§15.4): re-enqueues the step under the current
    // definition revision — mirrors the publish sweep's REVISION_REVIEW
    // disposition for one step.
    const resumeParkedMutation = useMutation({
        mutationFn: ({ enrollmentStepID }) => resumeParkedAutomationStep({ enrollmentStepID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () => deleteEnrollment({ enrollmentID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
            if (router.canGoBack()) router.back();
            else redirectToDefault(router, '/automations/' + pathID);
        },
    });

    const refresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
    }, [queryClient, pathID]);

    const enrollmentSteps = enrollment?.EnrollmentSteps || [];
    const waitingManualStep = enrollmentSteps.find(s => s.status === 'WAITING_MANUAL');
    const waitingDelayStep = enrollmentSteps.find(s => s.status === 'WAITING');
    const currentStep = waitingManualStep || waitingDelayStep ||
        enrollmentSteps.find(s => s.status === 'IN_PROGRESS');
    const currentPathStep = steps.find(step => step.id === currentStep?.pathStepID);
    const currentTaskInfo = currentStep?.PathStep?.stepType === 'TASK'
        ? resolveTaskStepInfo(currentPathStep, taskLookups, enrollmentStepDetails, enrollment?.Person)
        : null;
    const currentTaskInstanceID = currentStep?.PathStep?.stepType === 'TASK' ? currentStep?.taskInstanceID : null;
    const { data: currentTaskInstance } = Query.taskInstances.byId({
        id: currentTaskInstanceID,
        options: { enabled: !!currentTaskInstanceID },
    });
    const failedStep = enrollmentSteps.find(s => s.status === 'FAILED');

    // ── v2 surfaces (§14.7/§14.9/§15.3/A22) ──
    const isV2 = path?.schemaVersion === 2;
    // Captured trigger input/event — immutable facts recorded at run start.
    const captured = useMemo(
        () => (isV2 && enrollment)
            ? describeCapturedInput(enrollment, { inputSchema: path?.inputSchema })
            : { fields: [], event: null, hasCapture: false },
        [isV2, enrollment, path?.inputSchema]);
    // Parked review step (REVISION_REVIEW / PROVIDER_UNCERTAIN / STEP_*).
    const reviewStep = useMemo(
        () => enrollmentSteps.find(s => isStepParkedForReview(s)),
        [enrollmentSteps]);
    const reviewWait = useMemo(
        () => (reviewStep ? describeStepWaitState(reviewStep, { steps }) : null),
        [reviewStep, steps]);
    // Explicit task wait (WAIT_FOR_TASK or a blocking TASK step under v2).
    const taskWaitStep = useMemo(
        () => enrollmentSteps.find(s => s.waitState?.kind === 'TASK'),
        [enrollmentSteps]);
    const taskWait = useMemo(
        () => (taskWaitStep ? describeStepWaitState(taskWaitStep, { steps }) : null),
        [taskWaitStep, steps]);
    const waitTaskInstanceID = taskWaitStep?.waitState?.taskInstanceID || null;
    const { data: waitTaskInstance } = Query.taskInstances.byId({
        id: waitTaskInstanceID,
        options: { enabled: isV2 && !!waitTaskInstanceID && !currentTaskInstanceID },
    });
    // Completed-step outputs — safe typed fields only (§16.2).
    const outputRows = useMemo(() => {
        if (!isV2) return [];
        return enrollmentSteps
            .filter(s => s.status === 'COMPLETED' && s.output)
            .map(s => ({
                id: s.id,
                name: s.PathStep?.name || steps.find(st => st.id === s.pathStepID)?.name || 'Step',
                fields: describeStepOutput(s, { steps }),
            }))
            .filter(r => r.fields.length > 0);
    }, [isV2, enrollmentSteps, steps]);

    const { completedCount, longestPathTotal, progressPercent } = useMemo(
        () => computePathProgress({ steps, edges, enrollmentSteps }),
        [steps, edges, enrollmentSteps]
    );

    const status = enrollment ? (STATUS_CONFIG[enrollment.status] || STATUS_CONFIG.ACTIVE) : null;
    const person = enrollment?.Person;
    // Personless v2 runs are honest: "No person", never a blank name (§14.1).
    const personName = person
        ? `${person.firstName || ''} ${person.lastName || ''}`.trim()
        : null;
    const modalTitle = personName || 'Run';
    const isActive = enrollment?.status === 'ACTIVE';
    const isPaused = enrollment?.status === 'PAUSED';

    const handleDelete = () => {
        showAlert({
            title: 'Delete Run',
            subtitle: 'This will remove the queued message and permanently delete this run. This cannot be undone.',
            confirmText: 'Delete',
            isDestructive: true,
            onConfirm: () => deleteMutation.mutate(),
        });
    };

    const renderHeader = () => (
        <View style={styles.headerCard}>
            <View style={styles.headerRow}>
                <Avatar size={48} profile={person} disableBorder />
                <View style={styles.headerInfo}>
                    <Text style={styles.headerName}>{personName || 'No person'}</Text>
                    <Text style={styles.headerPath}>{path?.name}</Text>
                </View>
                {status && (
                    <View style={styles.statusBadge(status.colorVariant)}>
                        <Text style={styles.statusBadgeText(status.colorVariant)}>{status.label}</Text>
                    </View>
                )}
            </View>

            {/* Progress bar */}
            <View style={styles.progressSection}>
                <View style={styles.progressBarContainer}>
                    <View style={styles.progressBarFill(progressPercent)} />
                </View>
                <Text style={styles.progressText}>{completedCount} / {longestPathTotal} steps completed</Text>
            </View>

            {/* Disabled path warning — a disabled path won't run any steps */}
            {path && !path.isActive && (
                <View style={styles.pathDisabledRow} testID="PathDisabledNotice">
                    <Text style={styles.pathDisabledText}>
                        This automation is disabled — steps will not run until it is re-enabled.
                    </Text>
                </View>
            )}

            {/* Current step */}
            {currentStep && (isActive || isPaused) && (
                <View style={styles.currentStepRow}>
                    <View style={styles.currentStepInfo}>
                        <Text style={styles.currentStepText} numberOfLines={1}>
                            {waitingDelayStep?.scheduledFor
                                ? `Delayed until ${new Date(waitingDelayStep.scheduledFor).toLocaleString('default', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                                : `Current: ${currentStep.PathStep?.name || 'Unknown step'}`}
                        </Text>
                        {currentStep.PathStep?.stepType === 'TASK' && !waitingDelayStep && (
                            <View style={styles.currentStepAssigneeRow}>
                                {currentTaskInfo?.actualAssignee && currentTaskInfo.assigneeName && (
                                    <Avatar size={20} profile={currentTaskInfo.actualAssignee} disableBorder />
                                )}
                                <Text style={styles.currentStepAssignee} numberOfLines={1} testID="WaitingTaskAssignee">
                                    {currentTaskInfo?.assigneeName
                                        ? `Waiting for ${currentTaskInfo.assigneeName} to complete task`
                                        : 'Waiting for task completion — unassigned'}
                                </Text>
                            </View>
                        )}
                    </View>
                </View>
            )}

            {/* v2: parked-for-review step (REVISION_REVIEW / PROVIDER_UNCERTAIN /
                STEP_REVIEW / STEP_REMOVED) — never looks like an ordinary wait. */}
            {isV2 && reviewWait && reviewStep && (isActive || isPaused) && (
                <View style={styles.reviewRow(reviewWait.tone)} testID="ParkedStepNotice">
                    <View style={styles.reviewTextWrap}>
                        <Text style={styles.reviewTitle(reviewWait.tone)}>{reviewWait.title}</Text>
                        {!!reviewWait.detail && (
                            <Text style={styles.reviewDetail(reviewWait.tone)}>{reviewWait.detail}</Text>
                        )}
                    </View>
                </View>
            )}

            {/* v2: explicit task wait — waiting-on-task state with deadline,
                or the resolved outcome once it lands (§14.7). */}
            {isV2 && taskWait && taskWaitStep && !reviewStep && (
                <View style={styles.reviewRow(taskWait.tone)} testID="TaskWaitNotice">
                    <View style={styles.reviewTextWrap}>
                        <Text style={styles.reviewTitle(taskWait.tone)}>{taskWait.title}</Text>
                        {!!taskWait.detail && (
                            <Text style={styles.reviewDetail(taskWait.tone)}>{taskWait.detail}</Text>
                        )}
                    </View>
                </View>
            )}

            {/* Current task card — tap to open / reassign */}
            {(currentTaskInstance?.id || waitTaskInstance?.id) && (
                <View style={styles.taskCardContainer}>
                    <TaskCard taskInstance={currentTaskInstance || waitTaskInstance} showEvent={false} personID={person?.id} fromScreen={`/automations/enrollment/${enrollmentID}`} />
                </View>
            )}

            {/* Failed step — shown even when another (parallel) step is still
                in progress so failures on one branch stay visible. */}
            {failedStep && (isActive || isPaused) && (
                <View style={styles.failedStepRow}>
                    <Text style={styles.failedStepText} numberOfLines={1}>
                        Failed: {failedStep.PathStep?.name || 'Unknown step'}
                    </Text>
                    {failedStep.errorMsg && (
                        <Text style={styles.failedStepError} numberOfLines={2}>{failedStep.errorMsg}</Text>
                    )}
                </View>
            )}

            {/* Step action buttons */}
            <View style={styles.actionRow}>
                {isV2 && reviewWait?.resolution === 'RESUME' && reviewStep && (
                    <ActionButton
                        text="Resume"
                        iconName="play-forward-outline"
                        primary
                        onPress={() => resumeParkedMutation.mutate({ enrollmentStepID: reviewStep.id })}
                        disabled={resumeParkedMutation.isPending}
                        testID="ResumeParkedStepButton"
                    />
                )}
                {isV2 && reviewWait?.resolution === 'CONTINUE_PAST' && reviewStep && isActive && (
                    <ActionButton
                        text="Continue Past"
                        iconName="play-forward-outline"
                        primary
                        onPress={() => advanceStepMutation.mutate({ enrollmentStepID: reviewStep.id })}
                        disabled={advanceStepMutation.isPending}
                        testID="ContinuePastStepButton"
                    />
                )}
                {isV2 && reviewWait?.resolution === 'RETRY' && reviewStep && (
                    <ActionButton
                        text="Retry"
                        iconName="refresh-outline"
                        primary
                        onPress={() => retryStepMutation.mutate({ enrollmentStepID: reviewStep.id })}
                        disabled={retryStepMutation.isPending}
                        testID="RetryUncertainStepButton"
                    />
                )}
                {failedStep && (isActive || isPaused) && (
                    <ActionButton
                        text="Retry"
                        iconName="refresh-outline"
                        onPress={() => retryStepMutation.mutate({ enrollmentStepID: failedStep.id })}
                        disabled={retryStepMutation.isPending}
                        testID="RetryStepButton"
                    />
                )}
                {waitingManualStep && isActive && (
                    <ActionButton
                        text="Continue"
                        iconName="play-forward-outline"
                        primary
                        onPress={() => advanceStepMutation.mutate({ enrollmentStepID: waitingManualStep.id })}
                        disabled={advanceStepMutation.isPending}
                    />
                )}
                {waitingDelayStep && isActive && (
                    <ActionButton
                        text="Skip Delay"
                        iconName="time-outline"
                        onPress={() => skipDelayMutation.mutate({ enrollmentStepID: waitingDelayStep.id })}
                        disabled={skipDelayMutation.isPending}
                    />
                )}
                {isActive && (
                    <SolidButton
                        text="Stop"
                        iconName="pause-outline"
                        iconBefore
                        size="sm"
                        type="warning"
                        onPress={() => pauseMutation.mutate()}
                        processing={pauseMutation.isPending}
                        disabled={pauseMutation.isPending}
                    />
                )}
                {isPaused && (
                    <SolidButton
                        text="Resume"
                        iconName="play-forward-outline"
                        iconBefore
                        size="sm"
                        type="success"
                        onPress={() => resumeMutation.mutate()}
                        processing={resumeMutation.isPending}
                        disabled={resumeMutation.isPending}
                    />
                )}
                <SolidButton
                    text="Delete"
                    iconName="trash-outline"
                    iconBefore
                    size="sm"
                    type="error"
                    variant="outlined"
                    onPress={handleDelete}
                    processing={deleteMutation.isPending}
                    disabled={deleteMutation.isPending}
                />
            </View>
        </View>
    );

    const renderFlow = () => isWeb ? (
        <View style={styles.flowEditorContainer}>
            <PathFlowEditor
                path={path}
                steps={steps}
                edges={edges}
                enrollmentStepStatus={enrollmentStepStatus}
                enrollmentStepDetails={enrollmentStepDetails}
                enrollee={person}
                outOfScopeBranches={outOfScopeBranches}
                readOnly
            />
        </View>
    ) : (
        <PathFlowTimeline
            steps={steps}
            edges={edges}
            enrollmentStepStatus={enrollmentStepStatus}
            enrollmentStepDetails={enrollmentStepDetails}
            enrollee={person}
            outOfScopeBranches={outOfScopeBranches}
            readOnly
        />
    );

    const renderContent = () => {
        if (isFetching && !path) {
            return (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" />
                </View>
            );
        }

        if (!enrollment) {
            return (
                <View style={styles.loadingContainer}>
                    <Text style={styles.emptyText}>Run not found</Text>
                </View>
            );
        }

        return (
            <ScrollView
                style={styles.scroll}
                contentContainerStyle={styles.content}
                refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refresh} />}
                showsVerticalScrollIndicator={false}
            >
                {renderHeader()}
                {isV2 && captured.hasCapture && (
                    <View style={styles.capturedCard} testID="CapturedInputCard">
                        <Text style={styles.capturedTitle}>Captured at start</Text>
                        <Text style={styles.capturedHint}>Recorded when the run started — kept as history even if the automation changed since.</Text>
                        {captured.fields.map(f => (
                            <View key={f.key} style={styles.capturedRow}>
                                <Text style={styles.capturedLabel}>{f.label}</Text>
                                <Text style={styles.capturedValue} numberOfLines={3}>{f.value}</Text>
                            </View>
                        ))}
                        {captured.event?.type && (
                            <View style={styles.capturedRow}>
                                <Text style={styles.capturedLabel}>Event</Text>
                                <Text style={styles.capturedValue}>
                                    {captured.event.type}
                                    {captured.event.sourceType ? ` · ${captured.event.sourceType}` : ''}
                                    {captured.event.occurredAt ? ` · ${new Date(captured.event.occurredAt).toLocaleString('default', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
                                </Text>
                            </View>
                        )}
                        {enrollment?.enrolledAt && (
                            <View style={styles.capturedRow}>
                                <Text style={styles.capturedLabel}>Started</Text>
                                <Text style={styles.capturedValue}>
                                    {new Date(enrollment.enrolledAt).toLocaleString('default', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                </Text>
                            </View>
                        )}
                    </View>
                )}
                {renderFlow()}
                {isV2 && outputRows.length > 0 && (
                    <View style={styles.capturedCard} testID="StepOutputsCard">
                        <Text style={styles.capturedTitle}>Step outputs</Text>
                        {outputRows.map(row => (
                            <View key={row.id} style={styles.outputStepBlock}>
                                <Text style={styles.outputStepName}>{row.name}</Text>
                                {row.fields.map(f => (
                                    <View key={f.field} style={styles.capturedRow}>
                                        <Text style={styles.capturedLabel}>{f.label}</Text>
                                        <Text style={styles.capturedValue} numberOfLines={3}>{f.value}</Text>
                                    </View>
                                ))}
                            </View>
                        ))}
                    </View>
                )}
            </ScrollView>
        );
    };

    return (
        <ModalWrapper title={modalTitle} cancelText="Close" noPadding>
            {renderContent()}
        </ModalWrapper>
    );
}

const styles = StyleSheet.create((theme) => ({
    scroll: { flex: 1 },
    content: { padding: 16, paddingBottom: 100 },
    loadingContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 40 },
    emptyText: { fontSize: 16, color: theme.colors.textSecondary },

    // Header card
    headerCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
        boxShadow: theme.shadows.card,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    headerInfo: { flex: 1, marginLeft: 14 },
    headerName: {
        fontSize: 18,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    headerPath: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },

    // Progress
    progressSection: { marginBottom: 12 },
    progressBarContainer: {
        height: 6,
        backgroundColor: theme.colors.border,
        borderRadius: 3,
        overflow: 'hidden',
        marginBottom: 6,
    },
    progressBarFill: (percent) => ({
        height: '100%',
        width: `${percent}%`,
        backgroundColor: theme.colors.success,
        borderRadius: 3,
    }),
    progressText: {
        fontSize: 12,
        color: theme.colors.textSecondary,
    },

    // Current step
    currentStepRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: theme.colors.warningContainer,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        marginBottom: 12,
    },
    taskCardContainer: {
        marginTop: -6,
        marginBottom: 8,
    },
    currentStepInfo: {
        flex: 1,
        gap: 2,
    },
    currentStepText: {
        fontSize: 13,
        fontFamily: theme.fonts.bold,
        color: theme.colors.warning,
    },
    currentStepAssigneeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    currentStepAssignee: {
        flex: 1,
        fontSize: 12,
        color: theme.colors.warning,
    },

    // Failed step
    failedStepRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 8,
        backgroundColor: theme.colors.errorContainer,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        marginBottom: 12,
    },
    failedStepText: {
        fontSize: 13,
        fontFamily: theme.fonts.bold,
        color: theme.colors.error,
        flex: 1,
    },
    failedStepError: {
        fontSize: 12,
        color: theme.colors.errorMuted,
        flexBasis: '100%',
    },

    // Parked/review + task-wait notices
    reviewRow: (tone) => ({
        backgroundColor: theme.colors[tone === 'error' ? 'errorContainer' : tone === 'success' ? 'successContainer' : 'warningContainer'],
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        marginBottom: 12,
    }),
    reviewTextWrap: { gap: 2 },
    reviewTitle: (tone) => ({
        fontSize: 13,
        fontFamily: theme.fonts.bold,
        color: theme.colors[tone === 'error' ? 'error' : tone === 'success' ? 'success' : 'warning'],
    }),
    reviewDetail: (tone) => ({
        fontSize: 12,
        color: theme.colors[tone === 'error' ? 'errorMuted' : tone === 'success' ? 'success' : 'warning'],
        lineHeight: 17,
    }),

    // Captured input / step outputs cards
    capturedCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        boxShadow: theme.shadows.card,
    },
    capturedTitle: {
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 4,
    },
    capturedHint: {
        fontSize: 11,
        color: theme.colors.textTertiary,
        lineHeight: 15,
        marginBottom: 8,
    },
    capturedRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingVertical: 6,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        gap: 12,
    },
    capturedLabel: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        flexShrink: 0,
        maxWidth: '40%',
    },
    capturedValue: {
        fontSize: 13,
        color: theme.colors.text,
        textAlign: 'right',
        flex: 1,
    },
    outputStepBlock: {
        paddingVertical: 6,
    },
    outputStepName: {
        fontSize: 13,
        fontFamily: theme.fonts.bold,
        color: theme.colors.text,
        marginBottom: 2,
    },

    // Disabled path notice
    pathDisabledRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: theme.colors.warningContainer,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        marginBottom: 12,
    },
    pathDisabledText: {
        flex: 1,
        fontSize: 12,
        color: theme.colors.warning,
    },

    // Actions
    actionRow: {
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'wrap',
        alignItems: 'center',
    },

    // Status badge
    statusBadge: (colorVariant) => ({
        backgroundColor: theme.colors[colorVariant + 'Container'] || theme.colors.surfaceVariant,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 20,
    }),
    statusBadgeText: (colorVariant) => ({
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors[colorVariant] || theme.colors.textSecondary,
    }),

    // Flow
    flowEditorContainer: {
        _web: { height: 520, minHeight: 320 },
    },
}));
